import React from "react"
import {useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import {useKlaraDealDetailQuery, useKlaraUpdateCenterQuery} from "../../../queries/klaraDashboard.ts"
import {
  AKlaraDashboardDealDetail,
  AKlaraDashboardLastUpdateEntryTypeEnum,
  AKlaraDashboardSalesFlowTypeEnum,
  ASalesAssistantDealPriorities,
  ASalesCycleSalesFlows,
} from "../../../services/types.generated.ts"
import {enumTranslKey} from "../../../utils/i18n.tsx"
import {EMPTY_ARRAY, getFullName} from "../../../utils/index.ts"
import {SalesFlowBadge} from "../../DashboardSaaSCompany/SalesFlowBadge.tsx"
import {KlaraFiltersContext} from "../filters.tsx"
import {leadName} from "../leadName.ts"
import {formatSessionTime, TKlaraFeedEntry} from "../sessions.ts"
import {activityTypeLabel, displayStatus, formatActivityDate} from "../status.ts"
import {TDashboardClock, useDashboardClock} from "../useDashboardClock.ts"
import {CardState} from "./CardState.tsx"
import {Drawer} from "./Drawer.tsx"
import {SessionList} from "./SessionList.tsx"
import {StatusPill} from "./StatusPill.tsx"

const SALES_FLOW_BADGE: Record<AKlaraDashboardSalesFlowTypeEnum, ASalesCycleSalesFlows> = {
  [AKlaraDashboardSalesFlowTypeEnum.NewBiz]: ASalesCycleSalesFlows.NewBiz,
  [AKlaraDashboardSalesFlowTypeEnum.Upsell]: ASalesCycleSalesFlows.Upsell,
  [AKlaraDashboardSalesFlowTypeEnum.CrossSell]: ASalesCycleSalesFlows.CrossSell,
}

const PRIORITY_PILL_CLASS_NAME: Record<ASalesAssistantDealPriorities, string> = {
  [ASalesAssistantDealPriorities.High]: "bg-cr-red-pale text-cr-red-text",
  [ASalesAssistantDealPriorities.Medium]: "bg-cr-yellow-pale text-cr-yellow-deep",
  [ASalesAssistantDealPriorities.Low]: "bg-cr-slate-6 text-cr-grey-80",
}

const UPPERCASE_PILL_CLASS_NAME =
  "rounded-full px-[9px] py-[3px] text-[10px] font-bold tracking-[0.5px] whitespace-nowrap uppercase"

const SECTION_CLASS_NAME = "border-b border-cr-slate-2 px-6 py-[18px] last:border-b-0"

const SECTION_LABEL_CLASS_NAME = "text-[11px] font-bold tracking-[0.9px] text-cr-slate-60 uppercase"

/** One lead with its flows, stage, activities and history; mounted only while open. */
export const LeadDrawer: React.FC<{dealId: number; onClose: () => void}> = ({dealId, onClose}) => {
  const {t} = useTranslation()
  const {scopeVersion} = KlaraFiltersContext.useContext()
  const {data, isPending, isError, isFetching, refetch} = useKlaraDealDetailQuery(dealId)
  const deal = data?.data.deal
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  // A new period or sales person leaves the drawer open; it starts again from the top.
  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [scopeVersion])

  return (
    <Drawer
      onClose={onClose}
      title={deal ? leadName(deal) : t("KlaraDashboard_LeadDrawer_Title")}
      headerContent={deal && <LeadHeader deal={deal} />}
      widthClassName={"w-[470px]"}
    >
      <div ref={scrollContainerRef} className={"flex scrollbar-slim grow flex-col overflow-y-auto"}>
        <CardState query={{isPending, isError, isFetching, refetch}} skeletonClassName={"m-6 h-64 w-auto"}>
          {deal && <LeadDetails deal={deal} />}
        </CardState>
        <LeadHistory dealId={dealId} />
      </div>
    </Drawer>
  )
}

const LeadHeader: React.FC<{deal: AKlaraDashboardDealDetail}> = ({deal}) => {
  const {t} = useTranslation()
  const organisation = deal.organization?.name?.trim()
  // A lead named after its organisation doesn't repeat it under the name.
  const subtitle = [deal.contact?.position?.trim(), organisation !== leadName(deal) && organisation]
    .filter(Boolean)
    .join(" · ")

  return (
    <div className={"-mt-2 flex flex-col gap-2.5"}>
      {subtitle && <p className={"truncate text-[12.5px] text-cr-slate-60"}>{subtitle}</p>}
      <div className={"flex flex-wrap items-center gap-1.5"}>
        {deal.stage && (
          // A long stage name wraps inside the header, even through a long unbroken word.
          <span
            data-testid={"stage pill"}
            className={twMerge(
              UPPERCASE_PILL_CLASS_NAME,
              "max-w-full bg-cr-blue-light [overflow-wrap:anywhere] whitespace-normal text-cr-blue-dark"
            )}
          >
            {deal.stage.name}
          </span>
        )}
        <StatusPill status={displayStatus(deal)} className={"self-auto px-[9px] text-[11px]"} />
        <span className={twMerge(UPPERCASE_PILL_CLASS_NAME, PRIORITY_PILL_CLASS_NAME[deal.priority])}>
          {t("KlaraDashboard_LeadDrawer_Priority", {
            priority: t(enumTranslKey("SalesAssistantDealPriority", deal.priority)),
          })}
        </span>
      </div>
    </div>
  )
}

const Field: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div className={"flex min-w-0 flex-col gap-2"}>
    <dt className={SECTION_LABEL_CLASS_NAME}>{label}</dt>
    <dd className={"text-[13px] font-semibold text-cr-slate-90"}>{children}</dd>
  </div>
)

const LeadDetails: React.FC<{deal: AKlaraDashboardDealDetail}> = ({deal}) => {
  const {t} = useTranslation()
  const clock = useDashboardClock()

  return (
    <div className={"flex flex-col"}>
      <dl className={SECTION_CLASS_NAME}>
        <Field label={t("KlaraDashboard_LeadDrawer_Owner")}>
          <span className={"flex items-center gap-2"}>
            <span
              aria-hidden={"true"}
              className={
                "flex size-7 shrink-0 items-center justify-center rounded-full bg-cr-blue-light text-[10px] font-bold text-cr-blue-dark"
              }
            >
              {[deal.owner.first_name, deal.owner.last_name].map(part => part?.trim().charAt(0)).join("")}
            </span>
            {getFullName(deal.owner)}
          </span>
        </Field>
      </dl>
      <SalesFlows deal={deal} />
      <dl className={twMerge(SECTION_CLASS_NAME, "grid grid-cols-2 gap-4")}>
        <Field label={t("KlaraDashboard_LeadDrawer_LastUpdate")}>
          <LastUpdate deal={deal} clock={clock} />
        </Field>
        <Field label={t("KlaraDashboard_LeadDrawer_NextActivity")}>
          <NextActivity deal={deal} clock={clock} />
        </Field>
      </dl>
    </div>
  )
}

const SalesFlows: React.FC<{deal: AKlaraDashboardDealDetail}> = ({deal}) => {
  const {t} = useTranslation()
  const titleId = React.useId()
  const {stage} = deal

  return (
    <section aria-labelledby={titleId} className={twMerge(SECTION_CLASS_NAME, "flex flex-col gap-2")}>
      <h3 id={titleId} className={twMerge(SECTION_LABEL_CLASS_NAME, "mb-0.5")}>
        {t("KlaraDashboard_LeadDrawer_SalesFlows")}
      </h3>
      {deal.sales_flows.length > 0 && (
        <ul className={"flex flex-col gap-2"}>
          {deal.sales_flows.map(flow => (
            <li
              key={flow.id}
              className={"flex items-center gap-2 rounded-[10px] border border-cr-blue-light px-[13px] py-[11px]"}
            >
              <span className={"min-w-0 truncate text-[12.5px] font-bold text-cr-blue-dark"}>
                {flow.name?.trim() || t("KlaraDashboard_LeadDrawer_UnnamedSalesFlow")}
              </span>
              {flow.type !== null && <SalesFlowBadge salesFlow={SALES_FLOW_BADGE[flow.type]} />}
            </li>
          ))}
        </ul>
      )}
      {stage &&
        (stage.ordinal === null ? (
          <p className={"text-[11px] text-cr-grey-80"}>{stage.name}</p>
        ) : (
          // On a narrow drawer a long stage name wraps under the bar instead of squeezing it.
          <div className={"flex flex-wrap items-center gap-x-2 gap-y-1"}>
            <span aria-hidden={"true"} className={"h-1.5 min-w-24 grow overflow-hidden rounded-full bg-cr-slate-8"}>
              <span
                className={"block h-full rounded-full bg-cr-blue"}
                style={{width: `${(stage.ordinal / Math.max(stage.active_stages_count, 1)) * 100}%`}}
              />
            </span>
            <p className={"min-w-0 text-[11px] break-words text-cr-grey-80"}>
              {t("KlaraDashboard_LeadDrawer_StageProgress", {
                ordinal: stage.ordinal,
                total: stage.active_stages_count,
                stage: stage.name,
              })}
            </p>
          </div>
        ))}
    </section>
  )
}

const LastUpdate: React.FC<{deal: AKlaraDashboardDealDetail; clock: TDashboardClock}> = ({deal, clock}) => {
  const {t} = useTranslation()

  if (!deal.last_update) {
    return t("KlaraDashboard_LeadDrawer_NoUpdates")
  }

  const time = formatSessionTime(deal.last_update.tracked_at, {now: clock.asOf, timeZone: clock.timeZone})
  switch (deal.last_update.entry_type) {
    case AKlaraDashboardLastUpdateEntryTypeEnum.Voice:
      return t("KlaraDashboard_LeadDrawer_LastUpdateVia_Voice", {time})
    case AKlaraDashboardLastUpdateEntryTypeEnum.Assistant:
      return t("KlaraDashboard_LeadDrawer_LastUpdateVia_Assistant", {time})
  }
}

const NextActivity: React.FC<{deal: AKlaraDashboardDealDetail; clock: TDashboardClock}> = ({deal, clock}) => {
  const {t} = useTranslation()
  // The oldest overdue activity comes first; otherwise the earliest planned one.
  const activity = deal.overdue_activity ?? deal.next_activity

  if (!activity) {
    return t("KlaraDashboard_Leads_Issue_NothingPlanned")
  }

  return (
    <>
      {activityTypeLabel(activity.type)} · {formatActivityDate(activity.date, clock)}
      {deal.overdue_activity && (
        <span className={"text-cr-red-text"}> · {t("KlaraDashboard_Leads_Status_Overdue")}</span>
      )}
    </>
  )
}

const LeadHistory: React.FC<{dealId: number}> = ({dealId}) => {
  const {t} = useTranslation()
  const {scopeVersion} = KlaraFiltersContext.useContext()
  const titleId = React.useId()
  const {data, hasNextPage, isFetchingNextPage, fetchNextPage, isPending, isError, isFetching, refetch} =
    useKlaraUpdateCenterQuery({dealId})

  const entries = React.useMemo(
    () => data?.pages.flatMap<TKlaraFeedEntry>(page => page.data.update_center_entries) ?? EMPTY_ARRAY,
    [data?.pages]
  )

  // A new period or sales person leaves the drawer open; its history is fetched again.
  const firstScopeVersion = React.useRef(scopeVersion)
  React.useEffect(() => {
    if (scopeVersion !== firstScopeVersion.current) {
      void refetch()
    }
  }, [scopeVersion, refetch])

  const handleFetchNextPage = React.useCallback(() => void fetchNextPage(), [fetchNextPage])

  return (
    <section aria-labelledby={titleId} className={twMerge(SECTION_CLASS_NAME, "flex flex-col gap-2.5")}>
      <h3 id={titleId} className={SECTION_LABEL_CLASS_NAME}>
        {t("KlaraDashboard_LeadDrawer_History")}
      </h3>
      <CardState
        query={{isPending, isError, isFetching: isFetching && !isFetchingNextPage, refetch}}
        skeletonClassName={"h-32"}
      >
        {entries.length === 0 ? (
          <p className={"py-4 text-center text-[12.5px] text-cr-slate-60"}>
            {t("KlaraDashboard_LeadDrawer_HistoryEmpty")}
          </p>
        ) : (
          <SessionList
            variant={"timeline"}
            entries={entries}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            fetchNextPage={handleFetchNextPage}
          />
        )}
      </CardState>
    </section>
  )
}
